

import React from 'react';
import { motion } from 'framer-motion';
import { 
  Layers, Code, BookOpen, Search, Plus, Clock, FileText, 
  Settings as SettingsIcon, Palette, MessageSquare, Video, TrendingUp, 
  Headphones, Terminal, ChevronRight, ChevronDown, Lock, 
  Radar, Database, Target, Bell, Globe 
} from 'lucide-react';
import { RBACWrapper } from '../ControlBridge/OnboardingHub/RBACWrapper';
import { Button } from '../ui/button';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../ui/tooltip';





import RecentTasksDropdown from './RecentTasksDropdown';
import { useChatStore } from '../../stores/useChatStore';
import { useSidebar } from '../../contexts/SidebarContext';
import AutonomyToggle from './AutonomyToggle';

interface SidebarNavProps {
  activePanel?: string | null;
  setActivePanel?: (panel: string | null) => void;
  activeFeature?: 'workspace' | 'buildlab' | 'marketplace' | 'knowledge' | 'settings' | 'annotations' | 'controlbridge' | 'ticketfix' | 'ai-scout' | 'canvas' | 'syntara' | 'aex' | 'control-bridge' | 'support-bridge' | 'soma-chat' | 'hivectl-bridge' | 'buildstack' | 'soma' | 'abda';
  setActiveFeature?: (feature: 'workspace' | 'buildlab' | 'marketplace' | 'knowledge' | 'settings' | 'annotations' | 'controlbridge' | 'ticketfix' | 'ai-scout' | 'canvas' | 'syntara' | 'aex' | 'control-bridge' | 'support-bridge' | 'soma-chat' | 'hivectl-bridge' | 'buildstack' | 'soma' | 'abda') => void;
  onSearchClick?: () => void;
  onSearchTasksClick?: () => void;
  onSettingsClick?: () => void;
}

const SidebarNav: React.FC<SidebarNavProps> = ({
  activePanel,
  setActivePanel,
  activeFeature = 'workspace',
  setActiveFeature = () => {},
  onSearchClick,
  onSearchTasksClick,
  onSettingsClick,
}) => {
  const isOpen = true; // Replace with actual sidebar open state if needed
  const [executionModeCollapsed, setExecutionModeCollapsed] = React.useState(false);
  const [internalDashboardCollapsed, setInternalDashboardCollapsed] = React.useState(true);



  const renderWorkspaceButton = (buttonId: string) => {
    const buttonConfigs = {
      'buildlab': {
        icon: Layers,
        label: 'Build Lab',
        onClick: () => {
          setActiveFeature('buildlab');
        },
        isActive: activeFeature === 'buildlab',
        tooltip: 'Build Lab - Unified Build & Marketplace',
        gradient: 'from-orange-500 via-pink-500 to-blue-500 hover:from-orange-600 hover:via-pink-600 hover:to-blue-600'
      },
      'editor': {
        icon: Code,
        label: 'Editor',
        onClick: () => {
          setActiveFeature('workspace');
          if (setActivePanel) setActivePanel('editor');
        },
        isActive: activeFeature === 'workspace' && activePanel === 'editor',
        tooltip: 'Editor'
      },
      'knowledge': {
        icon: BookOpen,
        label: 'Knowledge Base',
        onClick: () => {
          if (activeFeature === 'workspace') {
            handleKnowledgeClick();
          } else {
            setActiveFeature('knowledge');
          }
        },
        isActive: activeFeature === 'knowledge' || (activeFeature === 'workspace' && activePanel === 'knowledge'),
        tooltip: 'Knowledge Base'
      },
      'control-bridge': {
        icon: Terminal,
        label: 'HiveCtl',
        onClick: () => {
          setActiveFeature('hivectl-bridge');
        },
        isActive: activeFeature === 'hivectl-bridge',
        tooltip: 'HiveCtl'
      },
      'onboarding-hub': {
        icon: Target,
        label: 'Success Bridge',
        onClick: () => {
          window.location.href = '/beehive/control-bridge/onboarding';
        },
        isActive: window.location.pathname.includes('/onboarding'),
        tooltip: 'Success Bridge - Client Portal & PM Cockpit'
      },
      'ai-scout': {
        icon: Search,
        label: 'AI Scout',
        onClick: () => setActiveFeature('ai-scout'),
        isActive: activeFeature === 'ai-scout',
        tooltip: 'AI Scout - Competitive Intelligence'
      },
      'abda': {
        icon: Globe,
        label: 'ABDA Browser',
        onClick: () => setActiveFeature('abda'),
        isActive: activeFeature === 'abda',
        tooltip: 'ABDA Browser - Automated Browser Data Acquisition'
      },
      'soma-chat': {
        icon: MessageSquare,
        label: 'Unified Chat',
        onClick: () => setActiveFeature('soma-chat'),
        isActive: activeFeature === 'soma-chat',
        tooltip: 'Unified Chat with SOMA & All Bees'
      }
    };

    const config = buttonConfigs[buttonId as keyof typeof buttonConfigs];
    if (!config) return null;

    const IconComponent = config.icon;

    return (
      <Tooltip key={buttonId}>
        <TooltipTrigger asChild>
          <Button 
            variant="ghost" 
            onClick={config.onClick}
            className={`relative w-full justify-start h-9 rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden focus:ring-2 focus:ring-blue-400 ${
              config.isActive
                ? buttonId === 'buildlab' 
                  ? 'text-white violett-button bg-gradient-to-r from-orange-500 via-pink-500 to-blue-500 hover:from-orange-600 hover:via-pink-600 hover:to-blue-600 hover:scale-105'
                  : 'text-white violett-button bg-gradient-to-r from-blue-700 to-transparent hover:from-blue-800 hover:to-transparent hover:scale-105'
                : buttonId === 'buildlab'
                  ? 'text-slate-300 hover:text-white hover:bg-gradient-to-r hover:from-orange-500/20 hover:via-pink-500/20 hover:to-blue-500/20 backdrop-blur-sm hover:shadow-lg hover:shadow-orange-500/20'
                  : 'text-slate-300 hover:text-white hover:bg-slate-800/50 backdrop-blur-sm hover:shadow-lg hover:shadow-blue-500/20'
            } ${isOpen ? 'px-3' : 'px-2 justify-center'}`}
          >
            {config.isActive && (
              <div className="absolute inset-0 bg-black/20 backdrop-blur-sm shadow-3xl"></div>
            )}
            <div className="flex items-center w-full">
              <IconComponent className="relative z-10 h-6 w-6" />
              {isOpen && <span className="relative z-10 ml-3 text-sm">{config.label}</span>}
            </div>
          </Button>
        </TooltipTrigger>
        {!isOpen && (
          <TooltipContent>
            <p>{config.tooltip}</p>
          </TooltipContent>
        )}
      </Tooltip>
    );
  };


  const handleNewTask = () => {
    // navigation logic for new task can be added here if needed
  };



  const handleFilesClick = () => {
    if (setActivePanel) setActivePanel(activePanel === 'files' ? null : 'files');
  };

  const handleKnowledgeClick = () => {
    if (setActivePanel) setActivePanel(activePanel === 'knowledge' ? null : 'knowledge');
  };

  const handleSearchClick = () => {
    if (setActivePanel) setActivePanel(activePanel === 'search' ? null : 'search');
  };



  return (
    <TooltipProvider>
      <motion.div 
        className={`relative h-full flex flex-col glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border-r border-slate-700/50 shadow-2xl transition-all duration-300 overflow-hidden ${
          isOpen ? 'w-[245px] p-4' : 'w-16 p-2'
        }`}
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ 
          duration: 0.4,
          type: "spring",
          stiffness: 500,
          damping: 30
        }}
      >
      {/* Card container with consistent spacing and staggered animations */}
      <motion.div 
        className="space-y-2"
        variants={{
          hidden: { opacity: 0 },
          visible: {
            opacity: 1,
            transition: {
              staggerChildren: 0.1,
              delayChildren: 0.2
            }
          }
        }}
        initial="hidden"
        animate="visible"
      >

      {/* Navigation Card */}
      <motion.div 
        className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl rounded-2xl shadow-2xl hover:shadow-indigo-700/10 p-4 space-y-1 transition-all duration-300"
        variants={{
          hidden: { x: -20, opacity: 0 },
          visible: {
            x: 0,
            opacity: 1,
            transition: {
              type: "spring",
              stiffness: 500,
              damping: 30
            }
          }
        }}
        whileHover={{ 
          y: -2,
          boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
        }}
      >
        {isOpen && (
          <h3 className="text-xs uppercase mb-2 cream-gradient-text text-drop-shadow">Navigation</h3>
        )}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button 
              variant="ghost" 
              onClick={handleNewTask}
              className={`w-full justify-start h-9 violett-button bg-gradient-to-r from-blue-700 to-transparent text-white hover:from-blue-800 hover:to-transparent rounded-xl shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-300 focus:ring-2 focus:ring-blue-400 ${
                isOpen ? 'px-3' : 'px-2 justify-center'
              }`}
            >
              <Plus className="h-4 w-4" />
              {isOpen && <span className="ml-3 text-sm">New Task</span>}
            </Button>
          </TooltipTrigger>
          {!isOpen && (
            <TooltipContent>
              <p>New Task</p>
            </TooltipContent>
          )}
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button 
              variant="ghost" 
              onClick={() => {}}
              className={`w-full justify-start h-9 text-slate-300 hover:text-slate-300/70 hover:bg-slate-800/30 rounded-lg shadow hover:shadow-lg transition-all duration-200 focus:ring-2 focus:ring-blue-400 ${
                isOpen ? 'px-3' : 'px-2 justify-center'
              }`}
            >
              <Search className="h-6 w-6" />
              {isOpen && <span className="ml-3 text-sm">Search Tasks</span>}
            </Button>
          </TooltipTrigger>
          {!isOpen && (
            <TooltipContent>
              <p>Search Tasks</p>
            </TooltipContent>
          )}
        </Tooltip>
        {isOpen ? (
          <div className="space-y-2">
            <RecentTasksDropdown />
          </div>
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="ghost" 
                className="w-full justify-center h-9 text-slate-300 hover:text-slate-300/70 hover:bg-slate-800/30 rounded-lg shadow hover:shadow-lg transition-all duration-200 focus:ring-2 focus:ring-blue-400 px-2"
              >
                <Clock className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Recent Tasks</p>
            </TooltipContent>
          </Tooltip>
        )}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button 
              variant="ghost" 
              onClick={handleFilesClick}
              className={`relative w-full justify-start h-9 rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden focus:ring-2 focus:ring-blue-400 ${
                activePanel === 'files'
                  ? 'text-white violett-button bg-gradient-to-r from-blue-700 to-transparent hover:from-blue-800 hover:to-transparent hover:scale-105'
                  : 'text-slate-300 hover:text-white hover:bg-slate-800/50 backdrop-blur-sm'
              } ${isOpen ? 'px-3' : 'px-2 justify-center'}`}
            >
              {/* Enhanced Overlay for gradient button */}
              {activePanel === 'files' && (
                <div className="absolute inset-0 bg-black/20 backdrop-blur-sm shadow-3xl"></div>
              )}
              <FileText className="relative z-10 h-4 w-4" />
              {isOpen && <span className="relative z-10 ml-3 text-sm">Files</span>}
            </Button>
          </TooltipTrigger>
          {!isOpen && (
            <TooltipContent>
              <p>Files</p>
            </TooltipContent>
          )}
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button 
              variant="ghost" 
              onClick={handleSearchClick}
              className={`relative w-full justify-start h-9 rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden focus:ring-2 focus:ring-blue-400 ${
                activePanel === 'search'
                  ? 'text-white violett-button bg-gradient-to-r from-blue-700 to-transparent hover:from-blue-800 hover:to-transparent hover:scale-105'
                  : 'text-slate-300 hover:text-white hover:bg-slate-800/50 backdrop-blur-sm'
              } ${isOpen ? 'px-3' : 'px-2 justify-center'}`}
            >
              {activePanel === 'search' && (
                <div className="absolute inset-0 bg-black/20 backdrop-blur-sm shadow-3xl"></div>
              )}
              <Search className="relative z-10 h-6 w-6" />
              {isOpen && <span className="relative z-10 ml-3 text-sm">Search</span>}
            </Button>
          </TooltipTrigger>
          {!isOpen && (
            <TooltipContent>
              <p>Search</p>
            </TooltipContent>
          )}
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button 
              variant="ghost" 
              onClick={() => {
                setActiveFeature('settings');
              }}
              className={`relative w-full justify-start h-9 rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden focus:ring-2 focus:ring-blue-400 ${
                activeFeature === 'settings'
                  ? 'text-white violett-button bg-gradient-to-r from-blue-700 to-transparent hover:from-blue-800 hover:to-transparent hover:scale-105'
                  : 'text-slate-300 hover:text-white hover:bg-slate-800/50 backdrop-blur-sm'
              } ${isOpen ? 'px-3' : 'px-2 justify-center'}`}
            >
              {/* Enhanced Overlay for gradient button */}
              {activeFeature === 'settings' && (
                <div className="absolute inset-0 bg-black/20 backdrop-blur-sm shadow-3xl"></div>
              )}
              <SettingsIcon className="relative z-10 h-6 w-6 text-violet-400 hover:text-violet-300" />
              {isOpen && <span className="relative z-10 ml-3 text-sm">Settings</span>}
            </Button>
          </TooltipTrigger>
          {!isOpen && (
            <TooltipContent>
              <p>Settings</p>
            </TooltipContent>
          )}
        </Tooltip>
      </motion.div>

      {/* Feature Navigation Card */}
      <motion.div 
        className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl rounded-2xl shadow-2xl hover:shadow-indigo-700/10 p-4 space-y-1 transition-all duration-300 overflow-y-auto max-h-96"
        variants={{
          hidden: { x: -20, opacity: 0 },
          visible: {
            x: 0,
            opacity: 1,
            transition: {
              type: "spring",
              stiffness: 500,
              damping: 30
            }
          }
        }}
        whileHover={{ 
          y: -2,
          boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
        }}
      >
        {isOpen && (
          <h3 className="text-xs uppercase mb-2 cream-gradient-text text-drop-shadow">Workspace</h3>
        )}
        
        <div className="space-y-1">
          {['abda', 'editor', 'soma-chat'].map((buttonId) => renderWorkspaceButton(buttonId))}
        </div>
        {/* Legacy workspace buttons - hidden for simplified UI */}
        {/* Uncomment to re-enable: Canvas, Syntara, AEX, Support Bridge */}

      </motion.div>



      {/* Execution Mode Card */}
      <div className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl rounded-2xl shadow-2xl hover:shadow-indigo-700/10 p-4 space-y-1 transition-all duration-300">
        {isOpen && (
          <button
            onClick={() => setExecutionModeCollapsed(!executionModeCollapsed)}
            className="flex items-center justify-between w-full text-xs uppercase mb-2 hover:opacity-70 transition-opacity"
            style={{ color: 'rgb(86, 156, 214)' }}
          >
            <span className="cream-gradient-text text-drop-shadow">Execution Mode</span>
            {executionModeCollapsed ? <ChevronRight className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          </button>
        )}
        {!executionModeCollapsed && (
          <div className="space-y-1">
            
            {/* Mode Switcher */}
            <div className="mt-2">
              <AutonomyToggle className="w-full" />
            </div>
          </div>
        )}
      </div>
      {/* Internal Dashboard Card */}
      <div className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl rounded-2xl shadow-2xl hover:shadow-red-500/10 p-4 space-y-1 transition-all duration-300">
        {isOpen && (
          <button
            onClick={() => {
              if (internalDashboardCollapsed) {
                const isAdmin = window.confirm("Admin authentication required. Please enter your credentials to access the Internal Dashboard.");
                if (isAdmin) {
                  setInternalDashboardCollapsed(false);
                }
              } else {
                setInternalDashboardCollapsed(true);
              }
            }}
            className="flex items-center justify-between w-full text-xs uppercase mb-2 hover:opacity-70 transition-opacity"
            style={{ color: 'rgb(239, 68, 68)' }}
          >
            <div className="flex items-center gap-2">
              <Lock className="w-3 h-3" />
              <span className="cream-gradient-text text-drop-shadow">Internal Dashboard</span>
            </div>
            {internalDashboardCollapsed ? <ChevronRight className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          </button>
        )}
        {!internalDashboardCollapsed && (
          <div className="space-y-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  variant="ghost" 
                  onClick={() => setActiveFeature('ai-scout')}
                  className={`relative w-full justify-start h-9 rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden focus:ring-2 focus:ring-blue-400 ${
                    activeFeature === 'ai-scout'
                      ? 'text-white violett-button bg-gradient-to-r from-blue-700 to-transparent hover:from-blue-800 hover:to-transparent hover:scale-105'
                      : 'text-slate-300 hover:text-white hover:bg-slate-800/50 backdrop-blur-sm hover:shadow-lg hover:shadow-blue-500/20'
                  } ${isOpen ? 'px-3' : 'px-2 justify-center'}`}
                >
                  {activeFeature === 'ai-scout' && (
                    <div className="absolute inset-0 bg-black/20 backdrop-blur-sm shadow-3xl"></div>
                  )}
                  <Radar className="relative z-10 h-6 w-6" />
                  {isOpen && <span className="relative z-10 ml-3 text-sm">AI Scout</span>}
                </Button>
              </TooltipTrigger>
              {!isOpen && (
                <TooltipContent>
                  <p>AI Scout</p>
                </TooltipContent>
              )}
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  variant="ghost" 
                  onClick={() => setActiveFeature('knowledge')}
                  className={`relative w-full justify-start h-9 rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden focus:ring-2 focus:ring-blue-400 ${
                    activeFeature === 'knowledge'
                      ? 'text-white violett-button bg-gradient-to-r from-blue-700 to-transparent hover:from-blue-800 hover:to-transparent hover:scale-105'
                      : 'text-slate-300 hover:text-white hover:bg-slate-800/50 backdrop-blur-sm hover:shadow-lg hover:shadow-blue-500/20'
                  } ${isOpen ? 'px-3' : 'px-2 justify-center'}`}
                >
                  {activeFeature === 'knowledge' && (
                    <div className="absolute inset-0 bg-black/20 backdrop-blur-sm shadow-3xl"></div>
                  )}
                  <Database className="relative z-10 h-6 w-6" />
                  {isOpen && <span className="relative z-10 ml-3 text-sm">Knowledge Base</span>}
                </Button>
              </TooltipTrigger>
              {!isOpen && (
                <TooltipContent>
                  <p>Knowledge Base</p>
                </TooltipContent>
              )}
            </Tooltip>
          </div>
        )}
      </div>









        {/* Notifications Admin - PM/Admin only */}
        <RBACWrapper userRole="pm" requiredPermission="notifications" requiredAction="admin">
          <motion.div
            className="mb-2"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Button
              variant="ghost"
              size="sm"
              className={`w-full justify-start text-left h-10 px-3 ${
                activeFeature === 'notifications-admin'
                  ? 'bg-blue-100 text-blue-700 border-blue-200'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
              onClick={() => setActiveFeature('notifications-admin')}
            >
              <Bell className="w-4 h-4 mr-3" />
              Notifications Admin
            </Button>
          </motion.div>
        </RBACWrapper>

      </motion.div>
    </motion.div>
    </TooltipProvider>
  );
};

export default SidebarNav;
